import { Badge, type BadgeProps } from '@mantine/core';
import { useFormErrorCounter } from './useFormErrorCounter';

/**
 * FormErrorCounter component displays a badge with the count of form errors.
 */
export type FormErrorCounterProps = BadgeProps & {
  /**
   * Which fields are watched for errors and should be counted.
   * If not provided, all fields will be counted.
   */
  fields?: string[];
};

/**
 * FormErrorCounter component
 * Counts the number of errors in a form and displays it in a badge.
 * When a form is free from errors, the badge will not be displayed.
 *
 * @param fields - Optional array of field names to count errors for.
 * @param props - Additional props for the Badge component.
 * @constructor
 */
export const FormErrorCounter: React.FC<FormErrorCounterProps> = ({
  fields,
  ...props
}) => {
  const errorCount = useFormErrorCounter(fields);

  return errorCount > 0 ? (
    <Badge
      variant="light"
      size="xs"
      color="red"
      mx={4}
      p={4}
      sx={{
        borderRadius: '8px',
      }}
      {...props}
    >
      {errorCount}
    </Badge>
  ) : null;
};
